import { useEffect, useRef, useCallback } from 'react';

interface UseModalDismissOptions {
  isOpen: boolean;
  onClose: () => void;
  /**
   * When true (typically while a mutation is pending) both Escape and
   * backdrop click are no-ops. Prevents the user from dismissing the modal
   * mid-submit and losing visibility into whether the action succeeded.
   */
  isBlocked?: boolean;
}

interface BackdropProps {
  onMouseDown: (e: React.MouseEvent) => void;
  onClick: (e: React.MouseEvent) => void;
}

interface UseModalDismissResult {
  backdropProps: BackdropProps;
}

/**
 * AC-1602 / AC-1593: shared dismiss behaviour for modals.
 *
 * - Escape key closes the modal while it is open (unless `isBlocked`).
 * - `backdropProps.onClick` + `backdropProps.onMouseDown` close only when
 *   BOTH the mousedown AND mouseup events landed on the backdrop element
 *   itself (target === currentTarget). This guards against the
 *   drag-select-overshoots-backdrop case (AC-1593): a user drag-selecting
 *   text inside an input may release the mouse outside the panel and onto
 *   the backdrop; a naive `onClick`-only check sees target=backdrop on the
 *   resulting click and would dismiss the modal unintentionally. Tracking
 *   mousedown's target via ref filters that out — a real backdrop dismiss
 *   has mousedown=mouseup=backdrop.
 * - On close, focus is restored to the element that held focus when the
 *   modal opened (typically the trigger button).
 */
export function useModalDismiss({
  isOpen,
  onClose,
  isBlocked = false,
}: UseModalDismissOptions): UseModalDismissResult {
  const previouslyFocusedRef = useRef<HTMLElement | null>(null);
  const mouseDownOnBackdropRef = useRef<boolean>(false);

  useEffect(() => {
    if (!isOpen) return;
    previouslyFocusedRef.current =
      document.activeElement instanceof HTMLElement
        ? document.activeElement
        : null;
    return () => {
      previouslyFocusedRef.current?.focus();
    };
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      if (isBlocked) return;
      onClose();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => {
      window.removeEventListener('keydown', onKeyDown);
    };
  }, [isOpen, isBlocked, onClose]);

  const onBackdropMouseDown = useCallback((e: React.MouseEvent) => {
    mouseDownOnBackdropRef.current = e.target === e.currentTarget;
  }, []);

  const onBackdropClick = useCallback(
    (e: React.MouseEvent) => {
      const startedOnBackdrop = mouseDownOnBackdropRef.current;
      mouseDownOnBackdropRef.current = false;
      if (isBlocked) return;
      if (e.target !== e.currentTarget) return;
      if (!startedOnBackdrop) return;
      onClose();
    },
    [isBlocked, onClose],
  );

  return {
    backdropProps: {
      onMouseDown: onBackdropMouseDown,
      onClick: onBackdropClick,
    },
  };
}
